import { onMounted, onUnmounted, toValue } from "vue";

import Fetch from "./Fetch";
import type { Options, Plugin, Result, Service } from "./types";
import { isEmpty } from "lodash-es";

function useRequestImplement<TData, TParams extends any[]>(
  service: Service<TData, TParams>,
  options: Options<TData, TParams> = {},
  plugins: Plugin<TData, TParams>[] = []
) {
  const { manual = false, ...rest } = options;

  const fetchOptions = {
    manual,
    ...rest
  };

  const initState = plugins.map((p) => p?.onInit?.(fetchOptions)).filter(Boolean);

  const fetchInstance = new Fetch<TData, TParams>(
    service,
    fetchOptions,
    Object.assign({}, ...initState)
  );

  fetchInstance.options = fetchOptions;
  // run all plugins hooks
  fetchInstance.pluginImpls = plugins.map((p) => p(fetchInstance, fetchOptions));

  onMounted(() => {
    if (!manual) {
      let params: any[] = toValue(options.defaultParams || []);
      if (!isEmpty(toValue(fetchInstance.params))) {
        params = toValue(fetchInstance.params);
      }
      // @ts-ignore
      fetchInstance.run(...params);
    }
  });

  onUnmounted(() => {
    fetchInstance.cancel();
  });

  return {
    loading: fetchInstance.loading,
    data: fetchInstance.data,
    error: fetchInstance.error,
    params: fetchInstance.params || [],
    cancel: fetchInstance.cancel.bind(fetchInstance),
    refresh: fetchInstance.refresh.bind(fetchInstance),
    refreshAsync: fetchInstance.refreshAsync.bind(fetchInstance),
    run: fetchInstance.run.bind(fetchInstance),
    runAsync: fetchInstance.runAsync.bind(fetchInstance),
    mutate: fetchInstance.mutate.bind(fetchInstance)
  } as Result<TData, TParams>;
}

export default useRequestImplement;
